import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionHead, Reveal } from "./Reveal";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PLAN_COPY = {
  plan_starter: { tag: "For trying it out.", perks: ["Background Swap, Person Swap and Image Generator", "Full-resolution downloads, no watermark", "Top up with credit packs"] },
  plan_creator: { tag: "For creators and shops.", perks: ["Everything in Starter", "3x the monthly credits"] },
  plan_pro: { tag: "For people who never stop.", perks: ["Everything in Creator", "Best price per credit"] },
};
const COSTS = ["Background Swap · 1 credit", "Person Swap · 1 credit", "Image Generator · 1 credit", "Video Swap · 10 credits"];
const money = (n) => (n == null ? "–" : `$${Number(n) % 1 ? Number(n).toFixed(2) : n}`);
const per = (price, credits) => (price && credits ? `$${(price / credits).toFixed(2)}` : "–");

const Tilt = ({ children, featured, badge, testId }) => {
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const rx = useSpring(useTransform(y, [0, 1], [5, -5]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(x, [0, 1], [-6, 6]), { stiffness: 200, damping: 20 });
  const glow = useTransform([x, y], ([a, b]) => `radial-gradient(420px circle at ${a * 100}% ${b * 100}%, rgba(200,206,214,${featured ? 0.3 : 0.16}), transparent 60%)`);
  return (
    <motion.div
      data-testid={testId}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - r.left) / r.width);
        y.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => { x.set(0.5); y.set(0.5); }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      className={`relative h-full rounded-2xl p-7 lg:p-8 bg-[#0b0b0d] border ${featured ? "border-[#d5d9df]/70 shadow-[0_30px_100px_-20px_rgba(190,197,208,0.7)]" : "border-white/10"}`}
    >
      <motion.div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ background: glow }} />
      {badge && <span className="absolute -top-3 left-7 badge-new">{badge}</span>}
      <div className="relative flex flex-col h-full">{children}</div>
    </motion.div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
    <div className="text-[10px] font-display tracking-[0.16em] uppercase text-slate-400">{label}</div>
    <div className="text-sm font-medium mt-0.5">{value}</div>
  </div>
);

const PlanCard = ({ p, user, busy, onBuy }) => {
  const featured = p.id === "plan_creator";
  const copy = PLAN_COPY[p.id] || { tag: "", perks: [] };
  return (
    <Tilt featured={featured} badge={featured ? "Most popular" : null} testId={`plan-card-${p.id}`}>
      <div className="h-display text-xl">{p.name}</div>
      <div className="mt-5 flex items-end gap-1.5">
        <span className="h-display text-5xl" data-testid={`plan-price-${p.id}`}>{money(p.price)}</span>
        <span className="text-slate-400 mb-1">/month</span>
      </div>
      <p className="text-sm text-slate-300 mt-3">{copy.tag}</p>
      <div className="mt-6 text-[10px] font-display tracking-[0.16em] uppercase text-slate-400">Credits</div>
      <div className="text-2xl font-semibold mt-1">{p.credits} swaps <span className="text-sm font-normal text-slate-400">a month</span></div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <Stat label="Per swap" value={per(p.price, p.credits)} />
        <Stat label="Tools" value="All 3" />
      </div>
      <ul className="mt-6 space-y-2.5 text-sm text-slate-200 flex-1">
        {copy.perks.map((t) => <li key={t} className="flex gap-2.5"><Check className="h-4 w-4 text-[#d5d9df] shrink-0 mt-0.5" />{t}</li>)}
      </ul>
      <button onClick={() => onBuy(p.id)} disabled={busy === p.id} data-testid={`plan-subscribe-${p.id}`} className={`btn-solid ${featured ? "" : "btn-white"} w-full py-4 mt-7`}>
        {busy === p.id ? <Loader2 className="h-5 w-5 animate-spin" /> : user?.plan === p.id ? "Current plan" : "Get started"}
      </button>
    </Tilt>
  );
};

const PackCard = ({ k, best, busy, onBuy }) => (
  <div data-testid={`pack-card-${k.id}`} className={`relative rounded-2xl p-6 bg-[#0b0b0d] border transition-[border-color,box-shadow] duration-300 hover:border-[#d5d9df]/60 ${best ? "border-[#d5d9df]/60 shadow-[0_20px_80px_-30px_rgba(190,197,208,0.8)]" : "border-white/10"}`}>
    {best && <span className="absolute -top-3 left-6 badge-new">Best value</span>}
    <div className="h-display text-4xl">{k.credits}</div>
    <div className="text-sm text-slate-400">credits</div>
    <div className="grid grid-cols-2 gap-2 mt-5">
      <Stat label="Swaps" value={`${k.credits} swaps`} />
      <Stat label="Per swap" value={per(k.price, k.credits)} />
    </div>
    <div className="flex items-center justify-between mt-6">
      <span className="h-display text-3xl">{money(k.price)}</span>
      <button onClick={() => onBuy(k.id)} disabled={busy === k.id} data-testid={`pack-buy-${k.id}`} className={`${best ? "btn-solid" : "btn-line"} px-5 py-3`}>
        {busy === k.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy"}
      </button>
    </div>
  </div>
);

export const usePlansFrom = () => {
  const [from, setFrom] = useState(null);
  useEffect(() => {
    api.get("/billing/catalog").then((r) => {
      const prices = (r?.data?.plans ?? []).map((p) => p?.price).filter((n) => n != null);
      setFrom(prices.length ? Math.min(...prices) : null);
    }).catch(() => {});
  }, []);
  return from;
};

export const Pricing = () => {
  const { user, openAuth } = useAuth();
  const [cat, setCat] = useState({ plans: [], packs: [] });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/catalog").then((r) => setCat({ plans: r?.data?.plans ?? [], packs: r?.data?.packs ?? [] })).catch(() => {});
  }, []);

  const bestPack = cat.packs.reduce((b, k) => (k.price && (!b || k.price / k.credits < b.price / b.credits) ? k : b), null)?.id;

  const buy = async (id) => {
    if (!user) return openAuth("register", "Create a free account first, then pick your plan.");
    setBusy(id);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: id, origin_url: window.location.origin });
      if (!data?.checkout_url) throw new Error("Checkout unavailable");
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errorText(e));
      setBusy(null);
    }
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-20 lg:py-28 overflow-hidden">
      <div className="absolute left-1/2 -translate-x-1/2 top-40 h-[600px] w-[900px] rounded-full bg-[#3b4049]/10 blur-[160px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8">
        <SectionHead center title="Pricing">Monthly plans with fresh credits every month. Or buy credits once, no subscription.</SectionHead>
        <div className="grid md:grid-cols-3 gap-5 pt-8">
          {cat.plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}><PlanCard p={p} user={user} busy={busy} onBuy={buy} /></Reveal>
          ))}
        </div>
        <Reveal>
          <div data-testid="credits-explainer" className="mt-5 panel p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="font-display font-semibold">How credits work</div>
              <p className="text-sm text-slate-400 mt-1">Photos use 1 credit and videos use 10. If the AI can't finish one, it comes straight back.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {COSTS.map((c) => <span key={c} className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02]">{c}</span>)}
            </div>
          </div>
        </Reveal>

        <SectionHead title="Need an affordable option?" className="mt-24 mb-10">Buy credits once. No subscription, and they never expire.</SectionHead>
        <div className="grid md:grid-cols-3 gap-5">
          {cat.packs.map((k, i) => (
            <Reveal key={k.id} delay={i * 0.08}><PackCard k={k} best={k.id === bestPack} busy={busy} onBuy={buy} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
